import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * `<OpsLogContainer />` — async wiring container composing
 * `IProjectionServiceClient` ops log + attestations fetch + Layer (6)
 * batch lookup + Phase 3 composer + `<OpsLogView />` rendering.
 *
 * **Phase 3 substantive composition gate IMPL — container wiring scaffold
 * per Account v0 home design memo §2.4 + §2.5 + Pattern B
 * no-parallel-rendering-surface discipline (composer composes upstream;
 * `<OpsLogView />` is pure rendering surface; this container bridges async
 * fetching + composition + rendering).** Composes against:
 *   - [PR #560](https://github.com/SmartHealthNetwork/SHN2026/pull/560) `composeOpsLogProvenance` Phase 3 composer scaffold
 *   - [PR #561](https://github.com/SmartHealthNetwork/SHN2026/pull/561) `<OpsLogView />` Phase 3 React UI scaffold
 *   - [PR #549](https://github.com/SmartHealthNetwork/SHN2026/pull/549) Layer (6) `IInclusionProofAttestationClient` wire-contract
 *   - [PR #517](https://github.com/SmartHealthNetwork/SHN2026/pull/517) `verifyAttestationBatch` (default verifier per composer signature)
 *
 * # Loading + error + success state machine
 *
 *   - `loading` — initial state + during async fetch + composition; renders
 *     loading indicator
 *   - `error` — async fetch or composition failed; renders error indicator
 *     (role=alert) per `feedback_phg_recovery_ux_honesty.md` content-honesty
 *     discipline analog (honest error framing; no false-success rendering)
 *   - `success` — composition complete; delegates rendering to
 *     `<OpsLogView />` with composed shape
 *
 * # Doctrinal anchors (Account v0 home §6 binding)
 *
 *   - NO SHT BACKDOOR (§6.1) — container fetches structural metadata only
 *     (ops log entries + attestations); cleartext key material does NOT
 *     flow through this surface
 *   - Recovery UX honesty (§6.2) — error state preserves honest framing;
 *     no false-success rendering on async failure
 *   - Pattern B no-parallel-rendering-surface (§6.3) — container is
 *     client-side React; composer + view are pure functions/components
 *     with substrate API client injection
 *   - Active-session-only crypto (§6.4) — `TrustedAttestationKeys` map
 *     session-resident (read from caller); container does NOT materialize
 *     cleartext keys; verifier composition uses non-extractable `CryptoKey`
 *     per ADR-032 §1
 *
 * # Phase 3 scaffold scope-fence
 *
 * v0 minimum-viable container — single-page fetch + render; pagination
 * cursor traversal substrate-state-event-paced (v1.5+ per Account v0
 * home §2.4 v1.5+ filter/search). AttestationKid v1.5+ joint-author
 * cycle (DEV5 + DEV6) typed brand at `verifiedKid` field substrate-state-
 * event-paced post Phase 3 React component IMPL ship per [DEV6 Phase 4
 * §F.1 row 3](file:///Users/paulmeyer/Desktop/CLAUDE%20TEAM/CTO/Substrate%20Engineering/Thread/00%20—%20Thread/2026-05-10%20—%20From%20DEV6%20—%20Phase%204%20SDK%20consumer%20surface%20scaffold%20design%20memo%20§Forward-going%20subsection%20pre-stage%20v0%20DRAFT%20%28three-brand%20cohort%20gates%20+%20Sprint%209+%20Layer%20chain%20+%20Phase%205%20chained%20composition%29.md).
 */
import * as React from 'react';
import { composeOpsLogProvenance, } from './compose-ops-log-provenance.js';
import { composeParticipantOpsLogProvenance } from './compose-participant-ops-log-provenance.js';
import { OpsLogView } from './ops-log-view.js';
export function OpsLogContainer(props) {
    const { client, subjectPciId, trustedAttestationKeys, tenantId, limit, cryptoAdapter, activeSession } = props;
    const [state, setState] = React.useState({ kind: 'loading' });
    React.useEffect(() => {
        let cancelled = false;
        async function fetchAndCompose() {
            try {
                const opsLog = await client.getPatientOpsLog({
                    subjectPciId,
                    tenantId: tenantId,
                    limit,
                });
                if (cancelled)
                    return;
                const recordIds = opsLog.entries.map((e) => e.recordId);
                const attestationsMap = recordIds.length > 0
                    ? await client.getInclusionProofAttestationsByRecordIds(recordIds)
                    : new Map();
                if (cancelled)
                    return;
                const composition = await composeOpsLogProvenance(opsLog.entries, attestationsMap, trustedAttestationKeys);
                if (cancelled)
                    return;
                setState({ kind: 'success', composition });
            }
            catch (err) {
                if (cancelled)
                    return;
                const message = err instanceof Error ? err.message : 'Unknown error';
                setState({ kind: 'error', message });
            }
        }
        void fetchAndCompose();
        return () => {
            cancelled = true;
        };
    }, [client, subjectPciId, trustedAttestationKeys, tenantId, limit]);
    // cryptoAdapter + activeSession are intentionally EXCLUDED from the fetch
    // effect dependency array — they affect participant-view decryption only,
    // which is gated on viewMode === 'participant' inside OpsLogViewWithToggle.
    // Suppressing the lint-rule signal here is correct per the seam separation.
    if (state.kind === 'loading') {
        return (_jsxs("section", { "aria-labelledby": "ops-log-heading", className: "ops-log-container ops-log-container--loading", "data-testid": "ops-log-container-loading", children: [_jsx("h2", { id: "ops-log-heading", children: "Ops log" }), _jsx("p", { className: "ops-log-container__loading", children: "Loading\u2026" })] }));
    }
    if (state.kind === 'error') {
        return (_jsxs("section", { "aria-labelledby": "ops-log-heading", className: "ops-log-container ops-log-container--error", "data-testid": "ops-log-container-error", role: "alert", children: [_jsx("h2", { id: "ops-log-heading", children: "Ops log" }), _jsxs("p", { className: "ops-log-container__error", children: ["Error: ", state.message] })] }));
    }
    return (_jsx(OpsLogViewWithToggle, { composition: state.composition, cryptoAdapter: cryptoAdapter, activeSession: activeSession }));
}
/**
 * Per-entry async decryption seam for participant-view per Phase 5 §C-iii
 * design memo §2. Returns `'no-payload'` for entries lacking the
 * envelope-encrypted payload fields (substrate-honest substrate-no-content
 * framing); `'capability-absent'` when cryptoAdapter / activeSession absent
 * OR KEK not loaded; `'decrypted'` on successful unwrap+AEAD-decrypt;
 * `'failed'` with substrate-honest reason on AEAD/unwrap failure.
 *
 * **OQ-2 status forward**: entry field names (`dekCiphertext` /
 * `payloadCiphertext` / `dekAad` / `payloadAad`) match the §C-iii design
 * memo §2 step 3 narrative. PR #887 §3 OQ-2 asks DEV6/SOL to canonical-name
 * these on the substrate-side wire-contract; this cycle-4 IMPL ships the
 * forward-compatible field names so the substrate-side extension lands
 * cleanly without client-side IMPL re-work.
 *
 * **`extractable: false` discipline** per ADR-032 §1: DEK unwrap explicitly
 * passes `extractable: false` (also the runtime default; defensive
 * redundancy here surfaces the doctrinal contract at this consumer-side
 * call site).
 *
 * **Active-session-only crypto** per Doctrine v1.4 §6.4 + feedback_phg_client_active_session_crypto.md:
 * decrypted bytes are decoded to UTF-8 strings and returned via the
 * `ParticipantPayloadStatus` discriminated union; caller (`composeParticipantOpsLogProvenance`
 * → `<OpsLogView />` participant-view rendering) holds the cleartext in
 * component-local state only; cleanup at toggle-back-to-substrate-view per
 * design memo §2 step 4.
 */
async function decryptEntryPayload(entry, cryptoAdapter, activeSession) {
    // OQ-2 placeholder: detect entry-has-payload by checking the optional
    // envelope-encrypted fields. At cycle-4 (v0), PatientOpsLogEntry has none
    // of these fields, so EVERY entry returns 'no-payload'. When OQ-2 lands
    // + substrate-side wire-contract extends with these fields, the same code
    // path lights up automatically.
    if (entry.dekCiphertext === undefined ||
        entry.payloadCiphertext === undefined ||
        entry.dekAad === undefined ||
        entry.payloadAad === undefined) {
        return { kind: 'no-payload' };
    }
    // Capability gate per design memo §2 step 2.
    if (cryptoAdapter === undefined || activeSession === undefined) {
        return { kind: 'capability-absent' };
    }
    if (activeSession.state !== 'started') {
        return { kind: 'capability-absent' };
    }
    let kekHandle;
    try {
        kekHandle = activeSession.requireKek();
    }
    catch {
        // ActiveSessionError ('kek-not-loaded' or 'session-ended') — both
        // surface as 'capability-absent' per substrate-honest framing.
        return { kind: 'capability-absent' };
    }
    try {
        const dek = await cryptoAdapter.unwrapKey({
            outerKey: kekHandle.key,
            wrappedBytes: entry.dekCiphertext,
            aad: entry.dekAad,
            extractable: false,
            usages: ['decrypt'],
        });
        const cleartextBytes = await cryptoAdapter.decryptRecord({
            dek,
            ciphertext: entry.payloadCiphertext,
            aad: entry.payloadAad,
        });
        const cleartext = new TextDecoder('utf-8').decode(cleartextBytes);
        return { kind: 'decrypted', cleartext };
    }
    catch (err) {
        const reason = err instanceof Error && err.name === 'EnvelopeCryptoAdapterError'
            ? err.code ?? 'authentication-failed'
            : 'authentication-failed';
        return { kind: 'failed', reason };
    }
}
/**
 * Compute the participant-view composition from the substrate-view composition
 * + decryption capability. Pure orchestration over `decryptEntryPayload` +
 * `composeParticipantOpsLogProvenance` (the latter is PR #913 §C-iii.a pure
 * shape-transform composer).
 */
async function buildParticipantComposition(substrateView, cryptoAdapter, activeSession) {
    const entries = substrateView.entries.map((pe) => pe.entry);
    const results = await Promise.all(entries.map(async (entry) => {
        const status = await decryptEntryPayload(entry, cryptoAdapter, activeSession);
        return [entry.recordId, status];
    }));
    const payloadResults = new Map(results);
    return composeParticipantOpsLogProvenance(substrateView, payloadResults);
}
/**
 * Phase 5 §C-iii.b — wraps `<OpsLogView />` with two-views toggle UX
 * extended with active-session-only participant-view decryption seam.
 * Per design memo §2: lazy per-entry decryption on `viewMode === 'participant'`
 * transition; substrate-honest "Decrypting…" loading state during effect;
 * cleanup at toggle-back-to-substrate-view (drop refs; let GC reclaim).
 *
 * Default view = substrate-view (payload-blind). Participant-view opt-in
 * via toggle; capability-absent framing when cryptoAdapter / activeSession
 * absent (OQ-1 lean (b) optional disposition; PR #887 §3).
 */
function OpsLogViewWithToggle(props) {
    // v0 scaffold uses hardcoded labels per Mode 12 §2.10 scaffold scope-fence;
    // i18n absorption substrate-state-event-paced firing post §C-ii substantive
    // composition cycle canonical preservation forward. Substrate-grade tier
    // discipline preserved at presentation primitive layer canonical preservation
    // forward (avoids LocaleProvider context dependency at binding test layer).
    const [viewMode, setViewMode] = React.useState('substrate');
    const [participantState, setParticipantState] = React.useState({ kind: 'idle' });
    React.useEffect(() => {
        let cancelled = false;
        if (viewMode !== 'participant') {
            // Cleanup at toggle-back-to-substrate-view per design memo §2 step 4 —
            // drop the decrypted composition reference; let GC reclaim. No explicit
            // zeroize (CryptoKey runtime-managed; cleartext strings GC-reclaimed).
            setParticipantState({ kind: 'idle' });
            return;
        }
        setParticipantState({ kind: 'computing' });
        void (async () => {
            try {
                const composition = await buildParticipantComposition(props.composition, props.cryptoAdapter, props.activeSession);
                if (cancelled)
                    return;
                setParticipantState({ kind: 'ready', composition });
            }
            catch (err) {
                if (cancelled)
                    return;
                const message = err instanceof Error ? err.message : 'Unknown error';
                setParticipantState({ kind: 'error', message });
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [viewMode, props.composition, props.cryptoAdapter, props.activeSession]);
    return (_jsxs("div", { className: "ops-log-container__with-toggle", "data-view-mode": viewMode, children: [_jsxs("div", { className: "ops-log-container__two-views-toggle", role: "tablist", "aria-label": "Audit chain view selector \u2014 substrate-view or participant-view", children: [_jsx("button", { type: "button", role: "tab", "aria-selected": viewMode === 'substrate', tabIndex: viewMode === 'substrate' ? 0 : -1, "data-testid": "ops-log-view-tab-substrate", className: viewMode === 'substrate'
                            ? 'ops-log-container__view-tab ops-log-container__view-tab--active'
                            : 'ops-log-container__view-tab', onClick: () => setViewMode('substrate'), children: "Substrate view" }), _jsx("button", { type: "button", role: "tab", "aria-selected": viewMode === 'participant', tabIndex: viewMode === 'participant' ? 0 : -1, "data-testid": "ops-log-view-tab-participant", className: viewMode === 'participant'
                            ? 'ops-log-container__view-tab ops-log-container__view-tab--active'
                            : 'ops-log-container__view-tab', onClick: () => setViewMode('participant'), children: "Participant view" })] }), viewMode === 'substrate' && _jsx(OpsLogView, { composition: props.composition }), viewMode === 'participant' && participantState.kind === 'computing' && (_jsx("p", { className: "ops-log-container__decrypting", "data-testid": "ops-log-container-decrypting", children: "Decrypting\u2026" })), viewMode === 'participant' && participantState.kind === 'error' && (_jsxs("p", { className: "ops-log-container__error", "data-testid": "ops-log-container-participant-error", role: "alert", children: ["Error: ", participantState.message] })), viewMode === 'participant' && participantState.kind === 'ready' && (_jsx(OpsLogView, { composition: props.composition, viewMode: "participant", participantComposition: participantState.composition }))] }));
}
//# sourceMappingURL=ops-log-container.js.map